// New approval gate for CBRE (previously PDF-download-only, no Firestore
// record at all — see Stage 1 plan Section 3). Mirrors the transactional
// immutable-registry pattern already proven by approveCortevaInvoice().
import { collection, doc, runTransaction, serverTimestamp } from "firebase/firestore";
import { auth, db } from "../firebase";
import { getActiveProfileInTransaction } from "../services/financeCore/clientTaxProfileService";
import { buildSnapshotFields, snapshotDocRef } from "../services/financeCore/invoiceSnapshotService";
import { parseMonthKey } from "../services/financeCore/monthKey";
import { cbreGstProblem } from "../services/financeCore/cbreGst";
import { assertPeriodOpen } from "../services/financeCore/periodLockService";
import { InvoiceOverrideRequiredError, buildCorrectionRecord, figureChanges } from "../services/financeCore/invoiceCorrection";

const CBRE_FIGURES = [
  ["invoiceAmount", "Invoice amount"],
  ["salesTax", "Sales tax"],
  ["totalAmount", "Invoice total"]
];

// Approving an invoice number that is already approved: identical figures are a no-op;
// different figures need { force: true, correctionReason } (see runWithCorrectionPrompt).
export async function approveCbreInvoice(invoice, invoiceDate, { force = false, correctionReason = "" } = {}) {
  const invoiceNumber = String(invoice?.invoiceNumber || "").trim();
  if (!invoiceNumber || invoiceNumber === "Unknown") throw new Error("This invoice group has no usable invoice number.");
  if (!invoiceDate) throw new Error("Enter the invoice date before approving.");
  const monthKey = parseMonthKey(invoiceDate);
  if (!monthKey) throw new Error("Enter a valid invoice date (YYYY-MM-DD).");
  if (!(Number(invoice?.totals?.totalAmount) > 0)) throw new Error("The invoice total must be greater than zero.");

  const gstProblem = cbreGstProblem(invoiceNumber, invoice.lineItems, invoice.totals);
  if (gstProblem) throw new Error(gstProblem);

  const registryId = encodeURIComponent(invoiceNumber.toUpperCase());
  const snapshotPayload = {
    invoiceNumber,
    monthKey,
    invoiceDate,
    poNumbers: invoice.poNumbers || [],
    lineItems: invoice.lineItems || [],
    totals: invoice.totals,
    status: "APPROVED",
    approvedBy: auth.currentUser?.uid || null,
    approvedByEmail: auth.currentUser?.email || null
  };

  let outcome = "approved";
  await assertPeriodOpen(monthKey);
  await runTransaction(db, async (transaction) => {
    outcome = "approved";
    const registryRef = doc(db, "cbre_invoice_registry", registryId);
    const registryDoc = await transaction.get(registryRef);
    let correctionRecord = null;
    let finalInvoiceDate = invoiceDate;
    if (registryDoc.exists()) {
      const existing = registryDoc.data();
      if (existing.monthKey !== monthKey) {
        throw new Error(`${invoiceNumber} is already approved for ${existing.monthKey}. Use a different invoice number.`);
      }
      const changes = figureChanges(CBRE_FIGURES, existing.totals, invoice.totals);
      if (!changes.length) {
        outcome = "unchanged";
        return;
      }
      const newTotal = Number(invoice.totals.totalAmount);
      if (!force) {
        throw new InvoiceOverrideRequiredError({ invoiceNumber, existingAmount: existing.totals?.totalAmount, newAmount: newTotal, changes });
      }
      // The invoice keeps the date it was first approved with, so receivable ageing does not move.
      finalInvoiceDate = existing.invoiceDate || invoiceDate;
      correctionRecord = buildCorrectionRecord({
        invoiceNumber, clientCode: "CBRE", monthKey, previousAmount: existing.totals?.totalAmount, newAmount: newTotal,
        changes, reason: correctionReason, user: auth.currentUser, correctedAt: serverTimestamp()
      });
      outcome = "corrected";
    }
    const taxProfile = await getActiveProfileInTransaction(transaction, "CBRE");
    const snapshotFields = buildSnapshotFields({
      clientCode: "CBRE",
      sourceCollection: "cbre_invoice_registry",
      sourceDocId: registryId,
      invoiceNumber,
      monthKey,
      accountingPeriod: monthKey,
      invoiceDate: finalInvoiceDate,
      taxProfile,
      sourceDoc: invoice
    });

    transaction.set(registryRef, {
      ...snapshotPayload,
      invoiceDate: finalInvoiceDate,
      ...(correctionRecord ? { correctionReason: correctionRecord.reason, correctedAtIso: new Date().toISOString() } : {}),
      approvedAt: registryDoc.exists() ? registryDoc.data().approvedAt : serverTimestamp(),
      updatedAt: serverTimestamp()
    }, { merge: true });
    if (correctionRecord) {
      // Marks the write as an authorized correction (the snapshot rule requires a reason).
      snapshotFields.correctionReason = correctionRecord.reason;
      transaction.set(snapshotDocRef(registryId), snapshotFields, { merge: true });
      transaction.set(doc(collection(db, "invoice_snapshot_corrections")), correctionRecord);
    } else {
      transaction.set(snapshotDocRef(registryId), snapshotFields);
    }
  });

  return { ...snapshotPayload, corrected: outcome === "corrected", unchanged: outcome === "unchanged" };
}
